import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { InferSignalProp } from "../../../pojo/Util";
import { SeAttendanceStateComponent } from "./se.attendance.state.component";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { s25Resource } from "../../../util/s25.resource";
import { UserprefService } from "../../../services/userpref.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { S25AttendanceHistoryComponent } from "./se.attendance.history.component";
import { Bind } from "../../../decorators/bind.decorator";
import { S25ContactProfileImageComponent } from "../contact/contact.profile.image.component";
import { SeAttendanceStore } from "./se.attendance.store";
import { attendanceState, AttendanceStateName } from "./attendance.service";

type Attendee = NonNullable<InferSignalProp<typeof SeAttendanceStore, "attendees">>[number];

@Component({
    standalone: true,
    selector: "s25-ng-attendee",
    template: `
        <s25-ng-contact-profile-image
            [contactId]="$safeNavigationMigration(attendee()?.contactId)"
            [imageUrl]="$safeNavigationMigration(attendee()?.contactProfileImage)"
            [name]="$safeNavigationMigration(attendee()?.contactName)"
        />

        <div class="contact-details">
            <div class="contact-header">
                <h4 class="contact-name">{{ attendee().contactName }}</h4>
                @if (!!attendee().orgRoles?.length) {
                    <p class="contact-roles">
                        @for (role of attendee().orgRoles; track role.roleId) {
                            <div class="contact-role">{{ role.name }}</div>
                        }
                    </p>
                }
            </div>

            @let email = attendee().contactEmail;
            @if (!!email) {
                <p class="contact-email">
                    <s25-ng-icon [type]="'mailFilled'" [size]="18" [ariaLabel]="false" />
                    <a [href]="'mailto:' + email">{{ email }}</a>
                </p>
            }

            @let phone = attendee().contactPhone;
            @if (!!phone) {
                <p class="contact-phone">
                    <s25-ng-icon [type]="'phone'" [size]="18" [ariaLabel]="false" />
                    <a [href]="'tel:' + phone">{{ phone }}</a>
                </p>
            }
        </div>

        <div class="right">
            <div class="occurrences">
                @for (res of attendee().reservations; track res.reservationId) {
                    <div class="occurrence">
                        @if (totalOccurrences() > 1) {
                            <span class="attendance-date">{{ res.reservationStart }}</span>
                        }
                        <s25-ng-attendance-state
                            class="attendance-state"
                            [state]="res.attendeeState"
                            [contactId]="attendee().contactId"
                            [occurrenceId]="res.reservationId"
                            [readonly]="readonly()"
                            [contactName]="attendee().contactName"
                            [occurrenceLabel]="totalOccurrences() > 1 ? res.reservationStart : undefined"
                        />
                    </div>
                }
            </div>
            @if (mode() === "checkin") {
                @if (!attendee().reservations[0].checkedIn) {
                    <s25-ng-button class="check-in" [type]="'none'" [onClick]="onCheckInClick">
                        <s25-ng-icon [type]="'thumbsUp'" [ariaLabel]="false" />
                        Check In
                    </s25-ng-button>
                }
                @if (attendee().reservations[0].attendeeState === attendanceState.CHECKED_IN) {
                    <s25-ng-button class="check-out" [type]="'none'" [onClick]="onCheckOutClick">
                        <s25-ng-icon [type]="'exit'" [ariaLabel]="false" />
                        Check Out
                    </s25-ng-button>
                }
            }
            <div class="history">
                <s25-ng-button [type]="'link'" [onClick]="onViewHistoryClick">View History</s25-ng-button>

                <s25-ng-modal #registrationHistoryModal [onBody]="true">
                    <ng-template #s25ModalBody>
                        <s25-ng-attendance-history
                            [contactId]="attendee().contactId"
                            [totalOccurrences]="totalOccurrences()"
                        />
                    </ng-template>
                </s25-ng-modal>
            </div>
        </div>
    `,
    styles: `
        :host {
            background: var(--color--surface-bg);
            border-radius: 16px;
            color: var(--color--text);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid var(--color--border);
            transition: all 0.3s ease;
            overflow: hidden;
            position: relative;
            display: flex;
            gap: 1.25rem;
            align-items: flex-start;
            padding: 0.5rem;
            align-items: center;
        }

        :host:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .contact-details {
            display: grid;
            row-gap: 0.25rem;
            align-self: start;
            flex-grow: 1;
            min-width: 0;
        }

        .contact-header {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
        }

        .contact-name {
            margin: 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: var(--color--text-strong);
            line-height: 1.2;
        }

        .contact-email,
        .contact-phone {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            color: var(--color--text-muted);
        }

        .contact-email a,
        .contact-phone a {
            color: var(--color--link-text);
            text-decoration: none;
            font-weight: 500;
            transition: color 0.2s ease;
        }

        .contact-email a:hover,
        .contact-phone a:hover {
            color: var(--color--link-text-hover);
            text-decoration: underline;
        }

        ::ng-deep .contact-email svg,
        ::ng-deep .contact-phone svg {
            opacity: 0.7;
            color: var(--color--text-muted);
        }

        .occurrences {
            display: grid;
            row-gap: 0.5em;
        }

        .occurrence {
            display: grid;
            grid-template-columns: 1fr 6em;
            column-gap: 0.5em;

            .attendance-date {
                justify-self: end;
            }

            .attendance-state {
                text-align: center;
                align-content: center;
                padding: 0;
                grid-column-start: 2;
            }
        }

        .contact-roles {
            display: flex;
            gap: 0.5rem;
            font-size: 0.8em;
            align-items: center;
            flex-wrap: wrap;

            .contact-role {
                background-color: var(--cn-blue);
                padding: 0.25em;
                border-radius: 3px;
                color: white;
                align-content: center;
                line-height: 0;
                height: 1.5em;
            }
        }

        .right {
            display: grid;
            gap: 0.25em;
            height: 100%;
            grid-template-rows: auto 1fr;

            .history {
                align-content: end;
                text-align: end;
            }
        }

        .check-in,
        .check-out {
            display: flex;
            justify-content: end;
            align-items: center;
            gap: 0.25rem;
            text-align: right;
        }

        .check-in {
            color: var(--color--registration);
        }

        .check-out {
            color: var(--color--danger);
        }

        @media (max-width: 768px) {
            :host {
                flex-direction: column;
                align-items: stretch;
                gap: 0.75rem;
                padding: 0.75rem;
            }

            :host:hover {
                transform: none;
            }

            :host > s25-ng-contact-profile-image {
                align-self: center;
            }

            .contact-details {
                text-align: center;
                margin: 0 auto;
            }

            .contact-header {
                justify-content: center;
            }

            .contact-name {
                font-size: 1.1rem;
            }

            .contact-email,
            .contact-phone {
                justify-content: center;
                font-size: 0.9rem;
            }

            .contact-roles {
                justify-content: center;
            }

            .right {
                width: 100%;
            }

            .occurrences {
                width: 100%;
            }

            .occurrence {
                grid-template-columns: 1fr auto;
            }

            .history {
                text-align: center !important;
            }

            .check-in,
            .check-out {
                justify-content: center;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25IconComponent,
        SeAttendanceStateComponent,
        S25ButtonComponent,
        S25ModalModule,
        S25AttendanceHistoryComponent,
        S25ContactProfileImageComponent,
    ],
})
export class AttendeeComponent {
    readonly attendee = input.required<Attendee>();
    readonly totalOccurrences = input.required<number>();
    readonly mode = input<"standard" | "checkin">("standard");

    readonly readonly = computed(
        () => !this.orgSecStore.canManageAttendees() && this.attendee().contactId !== this.contactId.value(),
    );
    readonly contactId = s25Resource({ loader: () => UserprefService.getContactId() });
    readonly registrationHistoryModal = viewChild("registrationHistoryModal", { read: S25ModalComponent });

    readonly orgSecStore = inject(SeOrgSecurityStore);
    readonly attendanceStore = inject(SeAttendanceStore);

    @Bind
    async onViewHistoryClick() {
        await this.registrationHistoryModal()?.open({
            title: this.attendee()!.contactName,
        });
    }

    @Bind
    async onCheckInClick() {
        await this.setState("CHECKED_IN");
    }

    @Bind
    async onCheckOutClick() {
        await this.setState("CHECKED_OUT");
    }

    async setState(state: AttendanceStateName) {
        const rsrvId = this.attendee().reservations[0].reservationId;
        await this.attendanceStore.setAttendance(state, {
            occurrences: [rsrvId],
            contactId: this.attendee().contactId,
        });
    }

    protected readonly attendanceState = attendanceState;
}
